import { AsyncPipe } from '@angular/common'
import { Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { MessageService } from 'primeng/api'
import { Button } from 'primeng/button'

import { Action, Resource } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { CheckAccessDirective } from '@ti/core/common/directives'
import { BrowserSessionStorage } from '@ti/core/common/storage'
import { selectState } from '@ti/core/common/utils'
import { AlertCircleIconComponent } from '@ti/core/icons/alert-circle'
import { CloseIconComponent } from '@ti/core/icons/close'
import { FleetsCountIconComponent } from '@ti/core/icons/fleets-count'
import { PlusIconComponent } from '@ti/core/icons/plus'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { FleetHeaderComponent } from '@ti/core/ui-kit/layout/components/fleet-header'

import {
  ACCOUNTS_TSP_SCOPE_PARAM,
  AccountsModel,
  parseAccountsScope,
  TSP_ID_LABEL,
} from '../../models/accounts.model'
import { AddAccountDialogComponent } from '../add/add-account-dialog.component'
import { EditAccountDialogComponent } from '../edit/edit-account-dialog.component'
import { AccountsGridComponent } from '../grid/accounts-grid.component'

@Component({
  selector: 'app-ti-accounts-list',
  templateUrl: 'accounts-list.component.html',
  styleUrls: ['accounts-list.component.scss'],
  imports: [
    FleetHeaderComponent,
    AsyncPipe,
    FleetsCountIconComponent,
    Button,
    CheckAccessDirective,
    PlusIconComponent,
    TranslatePipe,
    AccountsGridComponent,
    AddAccountDialogComponent,
    EditAccountDialogComponent,
    AlertCircleIconComponent,
    CloseIconComponent,
  ],
})
export class AccountsListComponent {
  protected readonly api = inject(ApiService)
  protected readonly router = inject(Router)
  protected readonly route = inject(ActivatedRoute)

  protected readonly model = inject(AccountsModel)
  protected readonly state = selectState(AccountsModel)

  protected readonly languageService = inject(LanguageService)
  protected readonly messageService = inject(MessageService)
  protected readonly sessionStorage = inject(BrowserSessionStorage)

  protected readonly Action = Action
  protected readonly Resource = Resource
  protected readonly TSP_ID_LABEL = TSP_ID_LABEL

  public constructor() {
    // The scope is rebuilt from the route on every emission rather than read once, so Back,
    // Forward, reload and open-in-new-tab all reproduce it. Injecting the model above already
    // restored the remembered search, and this runs before the model's deferred first load.
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.model.applyScope(parseAccountsScope(params.get(ACCOUNTS_TSP_SCOPE_PARAM)))
    })
  }

  protected clearScope(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [ACCOUNTS_TSP_SCOPE_PARAM]: null },
      queryParamsHandling: 'merge',
    })
  }
}
